import type { AnchorHTMLAttributes, ButtonHTMLAttributes, MouseEvent, ReactNode, Ref } from 'react'
import { Link, type LinkProps } from 'react-router'
import { cx } from '../cx'
import { Icon, type IconName } from '../icons'
import styles from './Button.module.css'
import { Spinner } from './Spinner'

export type ButtonVariant = 'primary' | 'secondary' | 'quiet' | 'danger'
/** md = 44px (every student control), lg = 48px (primary actions). */
export type ButtonSize = 'md' | 'lg'

interface LookProps {
  variant?: ButtonVariant
  size?: ButtonSize
  /** Icons always come with a visible label (docs/research/05 §5.2). */
  icon?: IconName
  iconEnd?: IconName
  fullWidth?: boolean
  children: ReactNode
}

function lookClass(
  {
    variant = 'secondary',
    size = 'md',
    fullWidth,
  }: Pick<LookProps, 'variant' | 'size' | 'fullWidth'>,
  className?: string,
) {
  return cx(styles.button, styles[variant], styles[size], fullWidth && styles.full, className)
}

function Face({
  icon,
  iconEnd,
  children,
  loading,
  size = 'md',
}: LookProps & { loading?: boolean }) {
  const iconSize = size === 'lg' ? 22 : 20
  return (
    <>
      {loading ? <Spinner size={iconSize} /> : icon ? <Icon name={icon} size={iconSize} /> : null}
      <span className={styles.label}>{children}</span>
      {iconEnd && <Icon name={iconEnd} size={iconSize} />}
    </>
  )
}

export interface ButtonProps
  extends LookProps, Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  /** Shows the stirring pot and ignores clicks, but keeps focus (unlike `disabled`). */
  loading?: boolean
  ref?: Ref<HTMLButtonElement>
}

export function Button({
  variant,
  size,
  icon,
  iconEnd,
  fullWidth,
  loading = false,
  className,
  children,
  type = 'button',
  onClick,
  ...rest
}: ButtonProps) {
  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    if (loading) {
      event.preventDefault()
      return
    }
    onClick?.(event)
  }
  return (
    <button
      type={type}
      className={lookClass({ variant, size, fullWidth }, className)}
      aria-busy={loading || undefined}
      aria-disabled={loading || undefined}
      onClick={handleClick}
      {...rest}
    >
      <Face icon={icon} iconEnd={iconEnd} size={size} loading={loading}>
        {children}
      </Face>
    </button>
  )
}

type RouterLinkProps = LookProps &
  Omit<LinkProps, 'children' | 'className'> & { className?: string }
type AnchorProps = LookProps &
  Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'children'> & { href: string; to?: never }

/**
 * A link that looks like a button. Pass `to` for in-app routes (React Router) or `href` for
 * full-page navigation (server routes such as Google sign-in, downloads, other sites).
 */
export function ButtonLink(props: RouterLinkProps | AnchorProps) {
  if ('href' in props && props.href !== undefined) {
    const { variant, size, icon, iconEnd, fullWidth, className, children, ...rest } = props
    return (
      <a className={lookClass({ variant, size, fullWidth }, className)} {...rest}>
        <Face icon={icon} iconEnd={iconEnd} size={size}>
          {children}
        </Face>
      </a>
    )
  }
  const { variant, size, icon, iconEnd, fullWidth, className, children, ...rest } =
    props as RouterLinkProps
  return (
    <Link className={lookClass({ variant, size, fullWidth }, className)} {...rest}>
      <Face icon={icon} iconEnd={iconEnd} size={size}>
        {children}
      </Face>
    </Link>
  )
}
